import { NextLink } from '@apeiron/temp'
import { TabContext, TabPanel } from '@mui/lab'
import { Box, styled, Tab, TabProps, Tabs } from '@mui/material'
import * as R from 'ramda'
import {
  FC,
  forwardRef,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useRef,
  useState
} from 'react'

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const TabsSection = styled(Box)`
  display: flex;
  width: 100%;
`

const TabsContainer = styled(Box)`
  display: flex;
  flex: 1;
`

const StyledTabs = styled(Tabs)`
  .Mui-disabled {
    opacity: 0.1 !important; // override js on mui
  }
  .MuiTabs-indicator {
    background-color: #ffb752;
    height: 3px;
  }
  background-color: #181a1a;
  border-radius: 12px;
  flex: 1;
`

const StyledTab = styled(Tab)`
  margin: 0px 20px;
  padding-top: 18px;
  text-transform: none;
`

const StyledTabPanel = styled(TabPanel)`
  padding: unset;
`

const TabLinkComponent = (props: LinkTabProps) => {
  const { url, label, ...rest } = props || {}

  const ref = useRef(null)

  return (
    <NextLink href={url} passHref>
      <StyledTab className='Custom-Tab' label={label} {...rest} ref={ref} />
    </NextLink>
  )
}

// eslint-disable-next-line react/display-name
const TabLink = forwardRef<HTMLDivElement, LinkTabProps>((props, ref) => {
  return <TabLinkComponent {...props} ref={ref} />
})

const ApeironTabs: FC<Props> = props => {
  const { className, children, index = '0', input, sideAction } = props

  const [tabIndex, setTabIndex] = useState<string>(index)

  const handleOnChange = useCallback((_: SyntheticEvent, value: string) => {
    setTabIndex(value)
  }, [])

  return (
    <Container className={className}>
      <TabContext value={tabIndex}>
        <TabsSection className='Custom-Tabs-Section'>
          <TabsContainer className='Custom-Tabs-Container'>
            <StyledTabs
              centered
              className='Custom-Tabs'
              value={tabIndex}
              orientation={'horizontal'}
              onChange={handleOnChange}
            >
              {R.map((data: ApeironTabsInput) => {
                const { disabled = false, label, url, value } = data

                if (disabled) {
                  return <StyledTab disabled label={label} key={value} />
                }

                return url ? (
                  <TabLink url={url} label={label} key={value} value={value} />
                ) : (
                  <StyledTab label={label} key={value} value={value} />
                )
              }, input)}
            </StyledTabs>
          </TabsContainer>
          {sideAction || children}
        </TabsSection>
        {R.map((data: ApeironTabsInput) => {
          const { panel, value } = data

          return (
            <StyledTabPanel
              className='Custom-Tab-Panel'
              key={value}
              value={value}
            >
              {panel}
            </StyledTabPanel>
          )
        }, input)}
      </TabContext>
    </Container>
  )
}

export type ApeironTabsInput = {
  disabled?: boolean
  label: ReactNode
  panel: ReactNode
  url?: string
  value: string
}

type LinkTabProps = TabProps & {
  url: string
}

type Props = {
  children?: ReactNode
  className?: string
  index?: string
  input: ApeironTabsInput[]
  sideAction?: ReactNode
}

export default ApeironTabs
